import { Box, Button, Card, CardContent, Chip, Stack, Typography, Modal, ModalDialog, ModalClose, Input, Textarea } from '@mui/joy';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LayoutContext } from '../context';
import { APIMyActiveDispatches, APIDispatchRespond, APIDispatchConfirm, APIDispatchDecline } from '../api';
import { arrivalError, dispatchLabels, responseLabels, responseArrival, formatDispatchInstant } from '../dispatchUi';

export default function SurveyorDispatchesPage() {
    const { account, tokenEmployee } = useContext(LayoutContext);
    const navigate = useNavigate();
    const [dispatches, setDispatches] = useState([]);
    const [selected, setSelected] = useState(null);
    const [day, setDay] = useState('');
    const [arrival, setArrival] = useState('');
    const [comment, setComment] = useState('');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const inFlight = useRef(false);
    const sequence = useRef(0);
    const load = useCallback(async () => {
        const seq = ++sequence.current;
        const result = await APIMyActiveDispatches(tokenEmployee);
        if (seq !== sequence.current) return;
        if (Array.isArray(result)) { setDispatches(result); setLoaded(true); }
        else setError(result?.message || 'Не удалось обновить заявки');
    }, [tokenEmployee]);
    useEffect(() => {
        load();
        const interval = setInterval(load, 10000);
        return () => { clearInterval(interval); sequence.current += 1; };
    }, [load]);
    const perform = async (operation, success) => {
        if (inFlight.current) return;
        inFlight.current = true; setBusy(true); setError('');
        try {
            const result = await operation();
            if (!result || result.status === 'error') setError(result?.message || 'Операция не выполнена');
            else success?.(result);
            await load();
        } catch (_) { setError('Ошибка связи. Обновите данные перед повтором.'); }
        finally { inFlight.current = false; setBusy(false); }
    };
    const openResponse = (dispatch) => {
        setSelected(dispatch); setDay(dispatch.offered_dates?.length === 1 ? dispatch.offered_dates[0] : '');
        setArrival(''); setComment(''); setError('');
    };
    const submit = () => {
        const problem = arrivalError(selected, day, arrival);
        if (problem) { setError(problem); return; }
        // Геопозиция необязательна и не влияет на очередь. Не блокируем ею отправку.
        perform(() => APIDispatchRespond(selected.id, {
            broadcast_started_at: selected.broadcast_started_at, chosen_date: day,
            arrival_time: arrival, comment,
        }, tokenEmployee), () => setSelected(null));
    };
    return <Box sx={{ p: 2, maxWidth: 760, mx: 'auto' }}>
        <Typography level="h3">Доступные заявки</Typography>
        {error && !selected && <Typography color="danger" role="alert">{error}</Typography>}
        <Button variant="plain" disabled={busy} onClick={load}>Обновить</Button>
        {loaded && !dispatches.length && <Typography>Сейчас новых приглашений нет.</Typography>}
        <Stack spacing={1.5}>
            {dispatches.map(d => {
                const mine = (d.responses || []).filter(r => String(r.employee?.id) === String(account?.id)).at(-1);
                const chosen = mine?.id === d.selected_response && mine?.status === 'accepted_by_manager';
                const open = ['broadcasting', 'reassign_needed'].includes(d.status);
                const mayRespond = open && (!mine || ['declined', 'withdrawn'].includes(mine.status));
                return <Card key={d.id}><CardContent>
                    <Typography level="title-md">Событие #{d.event}</Typography>
                    <Typography>{d.event_detail?.bitrix_deal?.full_address || 'Адрес не указан'}</Typography>
                    <Chip>{dispatchLabels[d.status] || d.status}</Chip>
                    <Typography level="body-sm">Окно: {d.offered_time_from?.slice(0, 5)}—{d.offered_time_to?.slice(0, 5)} ({d.event_timezone})</Typography>
                    {mine && <>
                        <Typography>{responseLabels[mine.status]}</Typography>
                        <Typography>{responseArrival(mine)}</Typography>
                    </>}
                    {d.timeout_at && <Typography level="body-sm">
                        {d.status === 'awaiting_confirmation' ? 'Подтверждение до' : 'Следующая проверка'}: {' '}
                        {formatDispatchInstant(d.timeout_at, d.event_timezone)} ({d.event_timezone})
                    </Typography>}
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {mayRespond && <Button disabled={busy} onClick={() => openResponse(d)}>Указать время прибытия</Button>}
                        {chosen && <Button color="success" disabled={busy} onClick={() => perform(
                            () => APIDispatchConfirm(d.id, tokenEmployee, { response_id: mine.id }),
                            () => navigate('/surveyor'),
                        )}>Подтвердить участие</Button>}
                        {mine && ['pending', 'accepted_by_manager'].includes(mine.status) && <Button color="danger" variant="outlined" disabled={busy}
                            onClick={() => perform(() => APIDispatchDecline(d.id, tokenEmployee, { response_id: mine.id }))}>
                            Отозвать отклик
                        </Button>}
                    </Stack>
                </CardContent></Card>;
            })}
        </Stack>
        <Modal open={!!selected} onClose={() => { if (!busy) setSelected(null); }}>
            <ModalDialog sx={{ width: 'min(460px, 95vw)' }}>
                <ModalClose disabled={busy} />
                <Typography level="h4">Время прибытия</Typography>
                <Typography level="body-sm">Местное время объекта: {selected?.event_timezone}. Выберите время внутри предложенного окна.</Typography>
                {error && <Typography color="danger" role="alert">{error}</Typography>}
                <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
                    {(selected?.offered_dates || []).map(value => <Button key={value} size="sm" disabled={busy}
                        variant={day === value ? 'solid' : 'outlined'} onClick={() => setDay(value)}>
                        {value.split('-').reverse().join('.')}
                    </Button>)}
                </Stack>
                <Typography>Прибытие в пределах {selected?.offered_time_from?.slice(0, 5)}—{selected?.offered_time_to?.slice(0, 5)}</Typography>
                <Input type="time" value={arrival} disabled={busy} onChange={e => setArrival(e.target.value)}
                    slotProps={{ input: { step: 60, min: selected?.offered_time_from?.slice(0, 5), max: selected?.offered_time_to?.slice(0, 5), 'aria-label': 'Точное время прибытия' } }} />
                <Textarea placeholder="Комментарий (необязательно)" disabled={busy} value={comment} onChange={e => setComment(e.target.value)} />
                <Button loading={busy} disabled={!!arrivalError(selected, day, arrival)} onClick={submit}>Отправить отклик</Button>
            </ModalDialog>
        </Modal>
    </Box>;
}